// The client's home screen: which year to work on.
//
// Every published year is offered, not just the current one, because a client
// who remembers something about an earlier return needs somewhere to put it.
// That is the whole difference from the paper process, which had a deadline
// and then nowhere to go.

import type { YearSummary, YearsResponse } from '../api/client.js';

const STATUS_TEXT: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  submitted: 'Sent to us',
  in_review: 'Being reviewed',
  queries: 'We have a question',
  complete: 'Complete',
};

export interface YearPickerProps {
  data: YearsResponse;
  clientName: string;
  onOpen: (year: number) => void;
  onSignOut: () => void;
  /** Opens the details screen. Reachable from here as well as the banner,
   *  because a client who wants to correct an address should not have to
   *  wait for something to be wrong before they can find the form. */
  onDetails: () => void;
}

export function YearPicker({
  data,
  clientName,
  onOpen,
  onSignOut,
  onDetails,
}: YearPickerProps): JSX.Element {
  const firstName = clientName.split(' ')[0] ?? clientName;

  return (
    <div className="years">
      <header className="appbar">
        <div className="brand brand--small">MyShoebox</div>
        <span />
        <button type="button" className="btn btn--quiet" onClick={onDetails}>
          Your details
        </button>
        <button type="button" className="btn btn--ghost" onClick={onSignOut}>
          Sign out
        </button>
      </header>

      <main className="container">
        <h1>Hello {firstName}</h1>
        <p className="muted">
          Pick a year to work on. You can stop and come back whenever you like — nothing is ever
          locked, and everything saves as you type.
        </p>

        <div className="yearlist">
          {data.years.map((year) => (
            <YearCard
              key={year.year}
              year={year}
              isCurrent={year.year === data.currentYear}
              onOpen={() => onOpen(year.year)}
            />
          ))}
        </div>

        {data.years.length === 0 && (
          <p className="notice notice--warn">
            There is no questionnaire available yet. Your accountant will be in touch.
          </p>
        )}
      </main>
    </div>
  );
}

function YearCard({
  year,
  isCurrent,
  onOpen,
}: {
  year: YearSummary;
  isCurrent: boolean;
  onOpen: () => void;
}): JSX.Element {
  return (
    <button type="button" className="yearcard" onClick={onOpen}>
      <span className="yearcard__year">{year.year}</span>

      <span className="yearcard__body">
        <span className="yearcard__title">
          {/* Spelled out, because FY numbering genuinely confuses people —
              "2026" is the year it ENDS in, not the year it covers. */}
          1 July {year.year - 1} – 30 June {year.year}
          {isCurrent && <span className="yearcard__badge">This year</span>}
        </span>

        <span className="yearcard__meta">
          <span className={`pill pill--${year.status}`}>
            {STATUS_TEXT[year.status] ?? year.status}
          </span>
          {year.attachmentCount > 0 && (
            <span>
              {year.attachmentCount} document{year.attachmentCount === 1 ? '' : 's'}
            </span>
          )}
        </span>
      </span>

      <span className="yearcard__chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
